import { RC, ResourcesService } from '@core/framework/resources.service';
import { EnumGridModes } from '@core/framework/sysenums.enum';
import { UiService } from '@core/framework/ui.service';
import { Utils } from '@core/helpers/Utils';
import { AppInjector } from '@core/injector/injector.module';
import { AppConfigService } from '@m4u-angular/configuration/app.config.service';
import { FrameworkService } from '@m4u-angular/core/framework/framework.service';
import { DxDataGridComponent } from 'devextreme-angular';
import { exportDataGrid } from 'devextreme/excel_exporter';
import { exportDataGrid as exportToPdf } from 'devextreme/pdf_exporter';
import { Format, formatDate, formatNumber, parseNumber } from 'devextreme/localization';
import { Column } from 'devextreme/ui/data_grid';
import { Workbook } from 'exceljs';
import saveAs from 'file-saver';
import { Subscription } from 'rxjs';
import { BaseComponent } from '../component/BaseComponent';
import { BaseEntity, BaseState } from '../entities/BaseEntities';
import { IBaseComponent, IBaseModuleComponent } from './../component/IBaseComponent';
import { jsPDF } from 'jspdf';
import { ScreenService } from '@m4u-angular/core/framework/screen.service';
import { DataLoadOptions } from '@m4u-angular/base/datalayer/DataLoadOptions';
import DataSource from 'devextreme/data/data_source';

export class GridCellEditHandlers {
    constructor(
        public BeforeEdit?: string,
        public AfterEdit?: string,
        public AfterEditPromise?: string,
        public AfterEditAsync?: string
    ) {}
}

export class GridCellEditParams {
    constructor(
        public value: any,
        public rowData: any,
        public changes: any,
        public rowIndex: number
    ) {}
}

export class GridCellEditAsyncParams {
    constructor(public value: any, public rowData: any, public rowIndex: number) {}
}

export class GridCellOptionsParams {
    constructor(public value: any, public rowData: any, public rowIndex: number) {}
}

export class GridCellOptionHandlers {
    public that?: any;
    constructor(public QueryEditCell?: string, public QueryShowCell?: string) {}
}

class DefferedPromise<T> extends Promise<T> {
    constructor() {
        let resolve, reject;
        super((_resolve, _reject) => {
            resolve = _resolve;
            reject = _reject;
        });
        this.Resolve = resolve;
        this.Reject = reject;
    }
    Resolve: (value?: T | PromiseLike<T>) => void;
    Reject: (reason?: any) => void;
}

export class GridExtender {
    public get gridAny(): any {
        return this.grid;
    }
    public get CurrentRowIndex(): number {
        return this.currentRowIndex;
    }

    public constructor(
        grid: DxDataGridComponent,
        mode: EnumGridModes,
        allowCustomization: boolean,
        allowAdvancedFilters: boolean,
        component: IBaseComponent,
        entityTypeName: string,
        keepSorting: boolean
    ) {
        this.grid = grid;
        this.Mode = mode;
        this.AllowCustomization = allowCustomization;
        this.AllowAdvancedFilters = allowAdvancedFilters;
        this.KeepSorting = keepSorting;
        this.Component = component;
        this.EntityTypeName = entityTypeName;
        this.isValid.Resolve(true);
    }
    public get CurrentEntityTypeName() {
        if (!this._entityTypeName) {
            if (!Utils.IsEmpty(this.EntityTypeName)) {
                this._entityTypeName = this.EntityTypeName;
            } else {
                const entityType = (this.Component as any)?.EntityType;
                if (!entityType) {
                    return;
                }

                const entityInstance = new entityType();
                this._entityTypeName = entityInstance && (entityInstance as BaseEntity).TypeName;
            }
        }
        return this._entityTypeName;
    }
    // Subscribtions
    private subscribtions: Subscription[] = [];

    // Other
    private MapEventsEdit: { [ColumnName: string]: GridCellEditHandlers } = {};
    private MapEventsOptions: { [ColumnName: string]: GridCellOptionHandlers } = {};
    private currentRowIndex: number;
    public Mode: EnumGridModes;
    private AllowCustomization: boolean;
    private AllowAdvancedFilters: boolean;
    private KeepSorting: boolean; 
    private BeforePrintDataGridOptions: any;
    private BeforePrintRefs: {
        body: any[];
        gridParent: HTMLElement | null;
        gridEl: HTMLElement | null;
    } = {
            body: [],
            gridEl: null,
            gridParent: null
        };

    private Component: IBaseComponent;
    private isValid: DefferedPromise<boolean> = new DefferedPromise<boolean>();
    public IsAutoResizingEnabled = true; // auto grid resizing
    public EntityTypeName: string;

    // Link main objects
    public grid: DxDataGridComponent;

    private _resourceService = AppInjector.get(ResourcesService);
    private _appConfigService = AppInjector.get(AppConfigService);

    private _entityTypeName: string;

    // InitCompactMode(compactModeValue: boolean) {
    //     const compactModeClass = 'grid-compact-mode';
    //     const gridClassList = this.grid.instance.element().classList;
    //     compactModeValue ? gridClassList.add(compactModeClass) : gridClassList.remove(compactModeClass);
    //     this.grid.instance.repaint();
    // }

    // Shortcut for grid editing
    public Get(ColumnName: string, RowIndex: number): any {
        if (this.IsColumnExists(ColumnName) && this.IsRowExists(RowIndex)) {
            return this.grid.instance.cellValue(RowIndex, ColumnName);
        }
    }

    // Shortcut for grid editing
    public Set(ColumnName: string, RowIndex: number, oValue: any) {
        if (this.IsColumnExists(ColumnName) && this.IsRowExists(RowIndex)) {
            this.grid.instance.cellValue(RowIndex, ColumnName, oValue);
        }
    }

    public AcceptChanges() {
        this.grid.instance.saveEditData();
    }

    public HasEditData() {
        return this.grid.instance.hasEditData();
    }

    public CancelEditData() {
        this.grid.instance.cancelEditData();
    }

    public SetGridFilter(ColumnName: string, ComparisonOperator: string, oValue: any) {
        this.grid.instance.filter([ColumnName, ComparisonOperator, oValue]);
    }

    public Refresh() {
        return this.grid.instance.refresh();
    }

    public GetSelectedRowKeys(): number[] {
        return this.grid.instance.getSelectedRowKeys();
    }

    // Shortcut for grid editing with changes object
    // Provides safe column checking
    // Returns data considering changes that were made but not saved yet
    public GetData(ColumnName: string, e: GridCellEditParams | any): any {
        if (this.IsColumnExists(ColumnName)) {
            if (e instanceof GridCellEditParams) {
                return e.changes[ColumnName] !== undefined
                    ? e.changes[ColumnName]
                    : e.rowData[ColumnName];
            } else {
                return e[ColumnName];
            }
        }
    }

    // Shortcut for grid editing with changes object
    // Provides safe column checking
    // Returns previous data for this column
    public GetPrevData(ColumnName: string, e: GridCellEditParams | GridCellEditAsyncParams): any {
        if (this.IsColumnExists(ColumnName)) {
            return e.rowData[ColumnName];
        }
    }

    // Shortcut for grid editing with rowData object
    // Provides safe column checking
    public SetData(ColumnName: string, oValue: any, e: GridCellEditParams | any) {
        if (this.IsColumnExists(ColumnName)) {
            if (e instanceof GridCellEditParams) {
                e.changes[ColumnName] = oValue;
            } else {
                e[ColumnName] = oValue;
            }
        }
    }

    // Shortcut for grid editing
    public GotoCell(ColumnName: string, RowIndex: number): any {
        if (this.IsColumnExists(ColumnName) && this.IsRowExists(RowIndex)) {
            return this.grid.instance.editCell(RowIndex, ColumnName);
        }
    }

    // Check if column exists
    public IsColumnExists(ColumnName: string): any {
        if (this.GetColumn(ColumnName) === undefined) {
            throw new Error(`Column ${ColumnName} does not exist`);
        }
        return true;
    }

    // Return column by name
    public GetColumn(ColumnName: string): any {
        return this.grid.columns.find((x: any) => x.dataField === ColumnName);
    }

    public SetColumnVisibility(ColumnName: string, visible: boolean): void {
        this.grid.instance.columnOption(ColumnName, 'visible', visible);
    }

    // Check if row exists
    public IsRowExists(RowIndex: number): any {
        const iCount: number = this.GetRowCount();
        if (RowIndex < 0 || (RowIndex > iCount && iCount !== -1)) {
            throw new Error(`Row ${RowIndex} does not exist`);
        }
        return true;
    }

    public GetRowCount(): number {
        const iCount: number = this.grid.instance.totalCount();
        return iCount === -1 ? this.grid.instance.getVisibleRows().length : iCount;
    }

    private IsRowsExists() {
        return (
            this.grid.instance.totalCount() > 0 || this.grid.instance.getVisibleRows().length > 0
        );
    }

    public async ExportExcel() {
        const framework = AppInjector.get(FrameworkService);
        const formats = [
            framework.CompanyInfo.NumberPrice,
            framework.CompanyInfo.NumberSum,
            framework.CompanyInfo.NumberRate,
            framework.CompanyInfo.NumberPercent
        ];
        const exportFormat = 'xlsx';
        const gridExport = (this.grid.export as any) ?? {};
        const { customizeExcelCell, onAfterExporting } = gridExport;

        this.grid.instance.beginUpdate();
        this.grid.instance.option('loadPanel.enabled', false);
        this.grid.instance.endUpdate();

        this.grid.instance.beginUpdate();
        // Trigger native export event
        this.grid.onExporting.emit({
            component: this.grid.instance,
            element: this.grid.instance.element(),
            format: exportFormat,
            selectedRowsOnly: false
        });

        setTimeout(async () => {
            const workbook = new Workbook();
            const worksheet = workbook.addWorksheet('Main sheet');

            function parseNumberValue(v: unknown, format?: Format) {
                if (format && typeof v === 'string') {
                    return parseNumber(v, format);
                }

                return v;
            }

            await exportDataGrid({
                component: this.grid.instance,
                worksheet: worksheet,
                ...this.grid.export,
                customizeCell: options => {
                    const { gridCell, excelCell } = options;
                    const { value, column, rowType } = gridCell;
                    const { dataType } = column;

                    const isGroupRow = rowType === 'group';
                    const isDataRow = rowType === 'data';
                    const isTotalRow = rowType === 'totalFooter';

                    if (dataType === 'number' && isGroupRow) {
                        excelCell.value = parseNumberValue(excelCell.value, column.format);
                    }

                    if (dataType === 'string' && isGroupRow) {
                        if (typeof value === 'string') {
                            const [, caption] = value.split(';');
                            excelCell.value = caption ?? value;
                        }
                    }

                    if (isDataRow && dataType === 'date' && column.format) {
                        excelCell.value = formatDate(value, column.format);
                    }

                    if (isDataRow || isTotalRow || isGroupRow) {
                        if (typeof column.format === 'string') {
                            excelCell.numFmt = column.format;

                            // TODO: need to keep number type as number. Aligment for number is right.
                            if (formats.includes(column.format)) {
                                excelCell.alignment = 'right';
                            }
                        }
                    }

                    if (customizeExcelCell) {
                        customizeExcelCell(options);
                    }
                }
            });

            const buffer = await workbook.xlsx.writeBuffer();
            if (onAfterExporting) {
                onAfterExporting({ component: this.grid.instance });
            }

            this.grid.instance.endUpdate();

            this.grid.instance.beginUpdate();
            this.grid.instance.option('loadPanel.enabled', 'auto');
            this.grid.instance.endUpdate();

            saveAs(
                new Blob([buffer], { type: 'application/octet-stream' }),
                `${this.grid.export?.texts?.exportTo}.${exportFormat}`
            );
        }, 0);
    }

    // Handler to remove current row
    RemoveCurrentRow(e) {
        if (!this.IsRowDeletable(e)) {
            return;
        }

        this.grid.instance.deleteRow(e.rowIndex);
    }

    // Handler for new row creation
    CreateNewRow() {
        if (!this.IsNewRowCreatable()) {
            return;
        }

        if (!this.grid.instance.hasEditData()) {
            this.grid.instance.addRow();
        } else {
            this.grid.instance
                .saveEditData()
                .then(() => this.grid.instance.addRow())
                .catch(() => {
                    AppInjector.get(UiService).InfoMessage(RC('msgFinishEditing'));
                });
        }
        this.grid.instance.focus(this.grid.instance.getCellElement(0, 0));
    }

    // Bind context this to the event. It cannot be done other way
    private BindHandler(that: any, ColumnName: string, sHandlerName: string) {
        if (sHandlerName === undefined) {
            return;
        }

        if (that[sHandlerName] === undefined) {
            throw new Error(`Invalid handler was specified for ${ColumnName}`);
        }
        that[sHandlerName] = that[sHandlerName].bind(that);
    }

    /*
     * Attach handler event:
     * We have 4 events to choose from:
     * 1 - Before event - can be used for custom validation and such (not sure if needed yet). Can return false to prevent default operation
     * 2 - After event - can be used to update e.changes (by G.EditSet) directly on the row. Can return false to prevent default operation
     * 3 - AfterPromise event - can be used to return a promise that devextreme grid built-in mechanism will resolve
     *     the promise can still set values using e.changes (throug G.EditSet) directly on the row
     * 4 - AfterAsync event - this one is fired after grid has finished processing, and value has been saved to the underlying row
     *     this can be used for operations that relly on the underlying row value for their processing
     *     This handler can not longer access the e.changes, but it has access to the rowIndex and can modify rows directly
     *     as any other operation using G.Set
     */
    public AttachColumnEditHandler(
        that: BaseComponent<BaseState>,
        ColumnName: string,
        Handlers: GridCellEditHandlers
    ) {
        // Make sure column and handler both exists
        if (!this.IsColumnExists(ColumnName)) {
            return;
        }

        this.MapEventsEdit[ColumnName] = Handlers;

        // Bind handlers
        this.BindHandler(that, ColumnName, Handlers.BeforeEdit);
        this.BindHandler(that, ColumnName, Handlers.AfterEdit);
        this.BindHandler(that, ColumnName, Handlers.AfterEditPromise);
        this.BindHandler(that, ColumnName, Handlers.AfterEditAsync);

        // Bind the event to the column
        const column = this.GetColumn(ColumnName);
        column.setCellValue = this.ColumnEditHandler.bind(this, that, ColumnName, undefined);
    }

    /*
     * Attach event handler:
     * We have 2 events to choose from:
     * 1 - QueryEditCell - handler should return false if cell should not be editable
     * 2 - QueryPaintCell - handler should return false if cell should not be visible
     */
    public AttachColumnOptionsHandler(
        that: BaseComponent<BaseState>,
        ColumnName: string,
        Handlers: GridCellOptionHandlers
    ) {
        // Make sure column and handler both exists
        if (!this.IsColumnExists(ColumnName)) {
            return;
        }

        this.MapEventsOptions[ColumnName] = Handlers;

        // Bind handlers
        Handlers.that = that;
        this.BindHandler(that, ColumnName, Handlers.QueryEditCell);
        this.BindHandler(that, ColumnName, Handlers.QueryShowCell);

        // Bind form.customizeItem function to allow hiding items in form
        // Perform the binding only once
        if (this.grid.editing.form === undefined) {
            this.grid.editing.form = {};
        }

        if (this.grid.editing.form.customizeItem === undefined) {
            this.grid.editing.form.customizeItem = this.ColumnCustomizeItemHandler.bind(this, that);
        }
    }

    // Hide items in form edit mode
    private ColumnCustomizeItemHandler(that, item) {
        // See that we have a handler
        const handlers: GridCellOptionHandlers = this.MapEventsOptions[item.dataField];
        if (handlers === undefined || handlers.QueryShowCell === undefined) {
            return;
        }

        // Execute handler
        const row = this.grid.instance.getVisibleRows()[this.currentRowIndex].data;
        const args = new GridCellOptionsParams(
            this.GetData(item.dataField, row),
            row,
            this.currentRowIndex
        );
        const bPaint = handlers.that[handlers.QueryShowCell](args);

        // Set value
        item.visible = bPaint;
    }

    // Raise dependant events when newData is set by the handler
    // This is done for consistancy - the same event handling mechanism can handle also value changes as a result of other event handlers,
    // just like it happens when using cellValue programatically from Async handler, or just outside code
    // This procedure determines the changes made based on the previous version of newData (originalDate) and calls the matching handlers
    // for each column that such change took place
    private RaiseDependantEvents(that, ColumnName, originalData, newData, currentRowData) {
        // Get list of values that were updated during this handler - compare new vs original
        const columnsUpdated: string[] = [];
        for (const key in newData) {
            if (
                key !== ColumnName &&
                (originalData === undefined || newData[key] !== originalData[key])
            ) {
                columnsUpdated.push(key);
            }
        }

        // Preserve state at this event
        originalData = Utils.CloneObject(newData);

        // Process only those that were updated
        for (const column of columnsUpdated) {
            // See if we have handlers for the updated column
            const handlers: GridCellEditHandlers = this.MapEventsEdit[column];
            if (handlers === undefined) {
                continue;
            }

            // Raise event
            this.ColumnEditHandler(
                that,
                column,
                originalData,
                newData,
                newData[column],
                currentRowData
            );
        }
    }

    private ColumnEditHandler(that, ColumnName, originalData, newData, value, currentRowData) {

        // Ignore initial settings
        // When user resets a test field, data is set to ""
        if (value === undefined) {
            return;
        }

        console.log(`Editing cell: ${ColumnName} new value: ${value}`);
        // if (ColumnName == 'ItemDescription') debugger;

        // Prepare
        const handlers: GridCellEditHandlers = this.MapEventsEdit[ColumnName];
        const args: GridCellEditParams = new GridCellEditParams(
            value,
            currentRowData,
            newData,
            undefined
        );

        // Execute the before event. If it returns false, exit
        if (handlers === undefined) {
            return;
        }

        if (handlers.BeforeEdit !== undefined) {
            const handlerBefore = that[handlers.BeforeEdit];
            if (!handlerBefore(args)) {
                return;
            }
        }

        // Set value just on the column that was really edited
        // (basically execute the default behaviour)
        if (originalData === undefined) {
            this.grid.instance.columnOption(ColumnName).defaultSetCellValue(newData, value);
        }

        // Execute after event.
        if (handlers.AfterEdit !== undefined) {
            const handlerAfter = that[handlers.AfterEdit];
            handlerAfter(args);
            this.RaiseDependantEvents(that, ColumnName, originalData, newData, currentRowData);
        }

        // Execute the after event asynchronically
        // This is to be used for cases when we need the editable value to be already set in the grid
        // In such case, any modifications that need to be done, should be done only using G.Set and not through changes, since
        // changes was already updated at this point.
        if (handlers.AfterEditAsync !== undefined) {
            const argsAsync = new GridCellEditAsyncParams(
                args.value,
                args.rowData,
                this.currentRowIndex
            );
            if (argsAsync.rowIndex !== -1) {
                const handlerAsync = that[handlers.AfterEditAsync];
                setTimeout(() => handlerAsync(argsAsync), 100);
            }
        }

        // Execute the after event.
        // Handler must call resolve() inside
        // It is here after the async event on purpose -
        // since if user has specified both the regular and async event, the regular event must return a promise
        // At any case, the order of the regular and async events is random and should not be relied upon
        if (handlers.AfterEditPromise !== undefined) {
            const handlerPromise = that[handlers.AfterEditPromise];
            return new Promise(resolve => {
                const finalize = () => {
                    this.RaiseDependantEvents(
                        that,
                        ColumnName,
                        originalData,
                        newData,
                        currentRowData
                    );
                    if (originalData === undefined) {
                        console.log(`Saving cell: ${ColumnName}`, newData);
                    }
                    resolve(true);
                };
                handlerPromise(args, finalize.bind(this));
            });
        }

        if (originalData === undefined) {
            console.log(`Saving cell: ${ColumnName}`, newData);
        }
    }

    // Returns whether new row can be created now
    public IsNewRowCreatable(): boolean {
        const method: any = this.grid.editing.allowAdding;
        let bResult;
        if (method instanceof Function) {
            bResult = method();
        } else {
            bResult = method;
        }
        return bResult;
    }

    // Returns whether row can be edited now
    public IsRowEditable(row: any): boolean {
        return this.IsRowEditableOrUpdatable(row, this.grid.editing.allowUpdating);
    }

    // Returns whether row can be deleted now
    public IsRowDeletable(row: any): boolean {
        return this.IsRowEditableOrUpdatable(row, this.grid.editing.allowDeleting);
    }

    // Check if the operation is allowed for the row
    // Executes the bound function
    private IsRowEditableOrUpdatable(e: any, method: any): boolean {
        let bResult: boolean;

        const args = {
            component: this.grid.instance,
            row: {
                rowType: e.rowType,
                data: e.data
            }
        };
        if (method instanceof Function) {
            bResult = method(args);
        } else {
            bResult = method;
        }
        return bResult;
    }

    // Enable editing on click
    private OnRowClick(e) {
        if (!this.IsRowEditable(e)) {
            return;
        }

        if (this.grid.instance.hasEditData()) {
            this.grid.instance.cancelEditData();
        }

        this.grid.instance.editRow(e.rowIndex);
    }

    // Preserve current row
    // getRowIndexByKey will return -1 if it's a new row. For cellValue purposes, we can use 0 for that
    private OnEditingStart(e) {
        this.currentRowIndex = e.component.getRowIndexByKey(e.key);
        if (this.currentRowIndex === -1) {
            this.currentRowIndex = 0;
        }
    }

    // When adding new row, set the row index
    private OnInitNewRow(e) {
        this.currentRowIndex = 0;
    }

    // Add readonly class to rows that cannot be edited
    private OnRowPrepared(e) {
        if (e.rowType !== 'data' && e.rowType !== 'detail') {
            return;
        }

        if (!this.IsRowEditable(e)) {
            e.rowElement.classList.add('dx-state-readonly');
        }
    }

    // Various adjustments to editor
    private OnEditorPreparing(e) {
        if (e.parentType !== 'dataRow') {
            return;
        }

        // See if we have handlers for the updated column
        const handlers: GridCellOptionHandlers = this.MapEventsOptions[e.dataField];
        if (handlers === undefined) {
            return;
        }

        // Execute handlers
        // If painting not allowed, editing is disabled as well
        const args = new GridCellOptionsParams(
            this.GetData(e.dataField, e.row.data),
            e.row.data,
            this.currentRowIndex
        );
        let bAllowEdit =
            handlers.QueryEditCell !== undefined
                ? handlers.that[handlers.QueryEditCell](args)
                : true;
        const bPaint =
            handlers.QueryShowCell !== undefined
                ? handlers.that[handlers.QueryShowCell](args)
                : true;
        if (!bPaint) {
            bAllowEdit = false;
        }
        if (bAllowEdit) {
            return;
        }

        // If both painting and editing is now allowed, and we're in cell mode, don't even create the editor
        // Otherwise, set it to readonly
        // This way, cells that are not pained will not show editor even when focusing on them
        if (this.grid.editing.mode === 'cell' && !bPaint) {
            e.cancel = true;
        } else {
            e.editorOptions.readOnly = true;
            e.editorOptions.tabIndex = -1;
        }
    }

    // Make cell as readonly and hide
    private OnCellPrepared(e) {
        // Handle rows only
        if (e.rowType !== 'data') {
            return;
        }

        // See if we have handlers for the updated column
        const handlers: GridCellOptionHandlers = this.MapEventsOptions[e.column.dataField];
        if (handlers === undefined) {
            return;
        }

        // Execute handler, and mark as readonly
        if (handlers.QueryEditCell !== undefined) {
            const args = new GridCellOptionsParams(
                this.GetData(e.column.dataField, e.row.data),
                e.row.data,
                e.rowIndex
            );
            const bAllowEdit = handlers.that[handlers.QueryEditCell](args);
            if (!bAllowEdit) {
                e.cellElement.classList.add('dx-state-readonly');
            }
        }

        // Execute handler, and mark as invisible
        if (handlers.QueryShowCell !== undefined) {
            const args = new GridCellOptionsParams(
                this.GetData(e.column.dataField, e.row.data),
                e.row.data,
                e.rowIndex
            );
            const bAllowEdit = handlers.that[handlers.QueryShowCell](args);
            if (!bAllowEdit) {
                e.cellElement.classList.add('m4u-hide-text');
            }
        }
    }

    // See if need to skip column
    private OnFocusCellChanging(e) {
        // See if moving forward or backward
        const bForward: boolean =
            e.newColumnIndex > e.prevColumnIndex || e.newRowIndex > e.prevRowIndex;

        // eslint-disable-next-line no-constant-condition
        while (true) {
            // See if this is a special column. Skip them
            const bSpecial = e.columns[e.newColumnIndex].type !== undefined ? true : false;
            if (!bSpecial) {
                // Get column focus is moving to
                const sColumnName: string = e.columns[e.newColumnIndex].dataField;

                // See if we have handlers for this column
                const handlers: GridCellOptionHandlers = this.MapEventsOptions[sColumnName];
                if (handlers === undefined) {
                    return;
                }

                // Execute handler. If editing is allowed, we can break execution
                const row = e.rows[e.newRowIndex].data;
                const args = new GridCellOptionsParams(
                    this.GetData(sColumnName, row),
                    row,
                    e.newRowIndex
                );
                const bAllowEdit: boolean = handlers.that[handlers.QueryEditCell](args);
                if (bAllowEdit) {
                    return;
                }
            }

            // When moving forward
            if (bForward) {
                // When not last column - move to next
                if (e.newColumnIndex < e.columns.length - 1) {
                    e.newColumnIndex += 1;
                } else {
                    e.newColumnIndex = 0;
                    e.newRowIndex += 1;
                }
            } else {
                // When not first column - move to prev
                if (e.newColumnIndex > 0) {
                    e.newColumnIndex -= 1;
                } else {
                    e.newColumnIndex = e.columns.length - 1;
                    e.newRowIndex -= 1;
                }
            }
        }
    }
    // !!!THIS IS WORKING BECAUSE OF PROMISE VALUE IS IMMUTABLE AFTER RESOLVE/REJECT
    // validation sequence is:
    // create new deferred for IsValid
    // trigger grid's saveEditData
    // if grid in EDIT mode
    //      then OnRowValidating will be triggered where IsValid will be resolved
    //      else saveEditData().then will be called and IsValid will be resolved;

    public ValidateGrid(): Promise<boolean> {
        this.isValid = new DefferedPromise<boolean>();
        this.grid.instance.saveEditData().then(() => this.isValid.Resolve(true));
        return this.isValid;
    }

    private OnRowValidating(e) {
        if (e.status === 'pending') {
            e.promise.then(result => this.isValid.Resolve(result.isValid));
        } else {
            this.isValid.Resolve(e.isValid);
        }
    }

    // Sync column visibility and order in form with the one set by the user
    private OnOptionChanged(e) {
        if (e.name === 'columns') {
            this.SetColumnsCaption();
        }

        if (e.name === 'columns' && (e.fullName.endsWith('visible') || e.fullName.endsWith('visibleIndex'))) {
            this.grid.instance.beginUpdate();
            for (let i = 0; i < this.grid.instance.columnCount(); i++) {
                const colAny = this.grid.instance.columnOption(i);

                this.grid.instance.columnOption(i, {
                    formItem: {
                        visible: colAny.visible,
                        visibleIndex: colAny.visibleIndex
                    }
                });
            }
            this.grid.instance.endUpdate();
        }
    }

    // Initialization entry point
    public InitGrid() {
        this.InitGridGeneral();
        switch (this.Mode) {
            case EnumGridModes.Editable:
                this.InitGridEditable();
                break;
            case EnumGridModes.Report:
                this.InitGridReport();
                break;
            case EnumGridModes.Lines:
                this.InitGridLines();
                break;
        }
    }

    // Generic stuff for all grids
    private InitGridGeneral() {
        this.grid.allowColumnReordering = this.AllowCustomization;
        this.grid.allowColumnResizing = this.AllowCustomization;

        const stateStoring = this.grid.stateStoring;
        this.grid.stateStoring = {
            storageKey:
                stateStoring?.storageKey || `${this.Component.ComponentStateName}.Grid.State`,
            enabled: this.AllowCustomization,
            keepSorting: this.KeepSorting            
        } as any;

        // Set file name for excel export
        if (!this.grid.export || this.grid.export?.texts?.exportTo === 'DataGrid') {
            let sFileName: string = this.Component.ComponentStateName.replace(
                'Component',
                ''
            ).replace('Grid', '');
            if (sFileName.endsWith('s')) {
                sFileName += 'es';
            } else {
                sFileName += 's';
            }
            this.grid.export = { texts: { exportTo: sFileName } }; // { fileName: sFileName };
        }

        // Add class to grid if it's clickable
        const grid = this.grid;
        this.subscribtions.push(
            this.grid.onInitialized.subscribe(e => {
                const gridRowClickEvent = grid.onRowClick;
                if ((gridRowClickEvent && gridRowClickEvent.observed) || grid.focusedRowEnabled) {
                    e.element.classList.add('grid-row-clickable');
                }

                // HACK use custom props for saving custom grid config
                (this.grid.instance as any).AllowCustomization = this.AllowCustomization;
                (this.grid.instance as any).AllowAdvancedFilters = this.AllowAdvancedFilters;
                (this.grid.instance as any).KeepSorting = this.KeepSorting;

                this.SetColumnsCaption();
            })
        );
    }

    private SetColumnsCaption() {
        const gridColumns = this.grid.instance.option('columns') || [];
        gridColumns.forEach((column: Column) => this.SetColumnCaption(column));
    }

    private SetColumnCaption(column: Column) {
        if (Utils.IsEmpty(this.CurrentEntityTypeName) || Utils.IsEmpty(column.dataField) || !Utils.IsEmpty(column.caption)) {
            return;
        }

        const resourceName = `BL.${this.CurrentEntityTypeName}.${column.dataField}`;
        const caption = this._resourceService.RC(resourceName, (this.Component as any as IBaseModuleComponent).ModuleName);
        this.grid.instance.columnOption(column.dataField, 'caption', caption);
    }

    public Print(): void {
        const screen = AppInjector.get(ScreenService);

        const doc = new jsPDF({
            orientation: 'l',
            putOnlyUsedFonts: true
        });

        doc.addFont('assets/fonts/Assistant-Regular.ttf', 'Assistant', 'normal');
        doc.addFont('assets/fonts/Assistant-Light.ttf', 'Assistant-Light', 'normal');
        doc.addFont('assets/fonts/Assistant-Medium.ttf', 'Assistant-Medium', 'normal');
        doc.addFont('assets/fonts/Assistant-Bold.ttf', 'Assistant', 'bold');
        doc.setTextColor('#294D92');

        const [title, printTitle, filterDescription] = this.GetPrintHeaderInfo();

        const arrFilterDescription = filterDescription.split('\n');
        let yPosition = 16;
        if (title) {
            doc.setFontSize(28);
            doc.setFont('Assistant-Medium');
            const titleWidth = this.GetTextWidth(title, 28, 500);
            const titleXPosition = screen.IsRTL()
                ? doc.internal.pageSize.width - 14 - titleWidth / doc.internal.scaleFactor
                : 14;
            doc.text(this.ReverseHebrewText(title), titleXPosition, yPosition);
            yPosition += 8;
        }

        doc.setFontSize(14);
        doc.setFont('Assistant-Light');
        if (printTitle) {
            const printTitleWidth = this.GetTextWidth(printTitle, 14, 300);
            const printTitleXPosition = screen.IsRTL()
                ? doc.internal.pageSize.width - 14 - printTitleWidth / doc.internal.scaleFactor
                : 14;
            doc.text(this.ReverseHebrewText(printTitle), printTitleXPosition, yPosition);
            yPosition += 8;
        }

        if (arrFilterDescription) {
            for (let i = 0; i < arrFilterDescription.length; i++) {
                if (Utils.NZ(arrFilterDescription[i]) === '') {
                    continue;
                }

                const filterDescriptionWidth = this.GetTextWidth(arrFilterDescription[i], 14, 300);
                const filterDescriptionXPosition = screen.IsRTL()
                    ? doc.internal.pageSize.width - 14 - filterDescriptionWidth / doc.internal.scaleFactor
                    : 14;
                doc.text(this.ReverseHebrewText(arrFilterDescription[i]), filterDescriptionXPosition, yPosition);
                if (i < arrFilterDescription.length - 1) {
                    yPosition += 8;
                }
            }
        }

        const printLogoPath = this.GetPrintLogoPath();
        const iconXPosition = screen.IsRTL() ? 14 : doc.internal.pageSize.width - 34;
        doc.addImage(printLogoPath, 'jpeg', iconXPosition, 8, 20, 20);

        doc.setFontSize(16);
        doc.setFont('Assistant');
        exportToPdf({
            jsPDFDocument: doc,
            component: this.grid.instance,
            topLeft: { x: 0, y: yPosition - 4 },
            customizeCell: ({ gridCell, pdfCell }) => {
                pdfCell.textColor = '#294D92';
                pdfCell.borderColor = '#D2DAE8';
                pdfCell.borderWidth = 1;
                pdfCell.drawLeftBorder = false;
                pdfCell.drawRightBorder = false;
                if (gridCell.rowType === 'header') {
                    pdfCell.backgroundColor = '#E5EAF2';
                    pdfCell.font = { name: 'Assistant', size: 10, style: 'bold' };
                }
                if (gridCell.rowType === 'group') {
                    const summaryCellValue =
                        gridCell.groupSummaryItems &&
                        gridCell.groupSummaryItems
                            .map(x => {
                                const column = gridCell.column as any;
                                return column && column.parseValue
                                    ? column.parseValue(x.value)
                                    : x.value;
                            })
                            .filter(x => x && x !== 0)
                            .map(x => formatNumber(x, gridCell.column?.format))
                            .join(';');
                    const lookupValue =
                        gridCell.column?.lookup &&
                        gridCell.column?.lookup.calculateCellValue(gridCell.value);
                    const complexCellValue =
                        gridCell.value && gridCell.value.includes && gridCell.value.includes(';')
                            ? gridCell.value.split(';')[1]
                            : gridCell.value;
                    const formattedValue =
                        gridCell.column?.dataType === 'date'
                            ? formatDate(complexCellValue, gridCell.column?.format)
                            : complexCellValue?.toString();
                    const resultValue = lookupValue || formattedValue;
                    pdfCell.text =
                        summaryCellValue && resultValue
                            ? resultValue
                            : summaryCellValue || resultValue;
                }
                if (
                    !screen.IsRTL() &&
                    gridCell.rowType === 'data' &&
                    gridCell.value &&
                    Utils.ContainsHebrewLetters(gridCell.value)
                ) {
                    pdfCell.text = this.ReverseHebrewText(gridCell.value);
                }
                const gridExport = this.grid.export as any;
                if (gridExport?.customizePdfCell) {
                    gridExport.customizePdfCell({ gridCell, pdfCell });
                }
            }
        }).then(() => {
            window.open(doc.output('bloburl').toString());
        });

        return;
    }

    private GetPrintHeaderInfo() {
        const Page = (this.Component as any).Page;
        if (!Page) {
            return ['', '', ''];
        }

        const title = Page.GetTitle();
        const printTitle = Page.OnGetPrintTitle();
        const filterDescription = Page.OnGetFilterDescription();

        return [title, printTitle, filterDescription];
    }

    private GetTextWidth(text: string, fontSize: number, fontWeight: number) {
        const div = document.createElement('div');
        div.innerHTML = text;
        div.style.fontSize = `${fontSize}px`;
        div.style.fontWeight = `${fontWeight}`;
        div.style.position = 'absolute';
        div.style.height = 'auto';
        div.style.width = 'auto';
        div.style.whiteSpace = 'nowrap';
        div.style.visibility = 'hidden';
        const appContainer = document.querySelector('.app-container');
        appContainer.appendChild(div);
        const textWidth = div.clientWidth + 1;
        appContainer.removeChild(div);
        return textWidth;
    }

    private ReverseHebrewText(text: string) {
        return !Utils.ContainsHebrewLetters(text) ? text : text.split('').reverse().join('');
    }

    private GetPrintLogoPath(): string {
        const appConfig = AppInjector.get(AppConfigService);

        const projectAssetsFolder = `assets/projects/${appConfig.AppConfig.ModuleName.toLowerCase()}`;
        const projectPrintLogoPath = `${projectAssetsFolder}/print-logo.png`;
        const customProjectPrintLogoPath =
            appConfig.M4UConfig.customBuild &&
            `${projectAssetsFolder}/print-logo_${appConfig.M4UConfig.customBuild}.png`;

        return customProjectPrintLogoPath && Utils.IsIconExists(customProjectPrintLogoPath)
            ? customProjectPrintLogoPath
            : Utils.IsIconExists(projectPrintLogoPath)
                ? projectPrintLogoPath
                : 'assets/icons/icon-96x96.png';
    }

    // Initialize report grid
    private InitGridReport() {
        // Show adaptive row for reports
        this.grid.columnHidingEnabled = false;
    }

    // Initialize editable grid
    private InitGridEditable() {
        //Show adaptive row for reports
        this.grid.columnHidingEnabled = false;
        
        //Add delete column that looks normal
        this.grid.columns = [
            {
                type: 'buttons',
                width: 32,
                allowReordering: false,
                allowResizing: false,
                buttons: [{ name: 'delete' }]
            }
        ];

        if (!this.grid.sorting) {
            this.grid.sorting = {};
        }
        this.grid.sorting.mode = 'none';

        if (!this.grid.paging) {
            this.grid.paging = {};
        }
        this.grid.paging.pageSize = 99999;

        // Subscribe to events
        this.subscribtions.push(this.grid.onRowClick.subscribe(e => this.OnRowClick(e)));
        this.subscribtions.push(this.grid.onEditingStart.subscribe(e => this.OnEditingStart(e)));
        this.subscribtions.push(this.grid.onInitNewRow.subscribe(e => this.OnInitNewRow(e)));
        this.subscribtions.push(
            this.grid.onEditorPreparing.subscribe(e => this.OnEditorPreparing(e))
        );
        this.subscribtions.push(
            this.grid.onFocusedCellChanging.subscribe(e => this.OnFocusCellChanging(e))
        );
        this.subscribtions.push(this.grid.onCellPrepared.subscribe(e => this.OnCellPrepared(e)));
        this.subscribtions.push(this.grid.onOptionChanged.subscribe(e => this.OnOptionChanged(e)));
        this.subscribtions.push(this.grid.onRowPrepared.subscribe(e => this.OnRowPrepared(e)));
        this.subscribtions.push(this.grid.onRowValidating.subscribe(e => this.OnRowValidating(e)));
    }

    private InitGridLines() {
        this.InitGridReport();

        // tslint:disable-next-line:no-unused-expression
        this.grid &&
            this.grid.instance &&
            (this.grid.instance as any).option({
                'selection.mode': 'single',
                'selection.deferred': true,
                hoverStateEnabled: true
            });
    }

    // Unsubscribe
    public DestroyGrid() {
        this.subscribtions.forEach(o => o.unsubscribe());
    }

    OnDoubleSizePopup(e, data) {
        if (this.grid.editing.mode === 'form') {
            return;
        }

        const iWidth: number = data.column.visibleWidth * 2;
        e.component._popup.option('width', iWidth);
    }

    public OnGridClick() {
        if (!this.IsRowsExists()) {
            this.CreateNewRow();
        }
    }

    public GetDataLoadOptions(): DataLoadOptions {
        const dataSource = this.grid?.dataSource as DataSource;
        if (!dataSource?.loadOptions) return;
        const gridLoadOptions = dataSource.loadOptions();
        gridLoadOptions.filter = this.grid.instance.getCombinedFilter()
        const dataLoadOptions = new DataLoadOptions(gridLoadOptions);
        return dataLoadOptions;
    }
}
